import { Cart } from 'shared/types/cart';
import { Variant } from 'shared/types/product';

/**
 * Finds a matching line item in the cart based on the given variant and configuration options
 *
 * @param cart - The shopping cart containing line items
 * @param variant - The product variant to match
 * @param options - Configuration options for the product
 * @param options.finish - The finish option name
 * @param options.sheen - The sheen option value
 * @param options.hasWirebrush - Boolean indicating if wirebrush is selected
 * @returns The matching line item or null if no match is found
 */
export const findMatchingLineItem = (
  cart: Cart,
  variant: Variant,
  options: {
    finish?: { name: string };
    sheen?: string;
    hasWirebrush?: boolean;
  },
) => {
  const { finish, sheen, hasWirebrush } = options;

  // Check if cart has line items
  if (!cart?.lineItems?.length) return null;

  // Find exact configuration match
  const exactMatch = cart.lineItems.find((item) => {
    // Basic SKU check
    if (item.variant?.sku !== variant.sku) return false;

    // Configuration compatibility check
    const hasConfigurations = finish?.name || sheen || hasWirebrush != null;
    if (!item.config && hasConfigurations) return false;

    // Check specific configurations
    if (finish?.name && item.config?.finish !== finish.name) return false;
    if (sheen && item.config?.sheen !== sheen) return false;

    if (hasWirebrush != null) {
      const wirebrushValue = !!item.config?.wirebrush;
      if (wirebrushValue !== hasWirebrush) return false;
    }

    return true;
  });

  // If exact match with price found, return it
  if (exactMatch && exactMatch.price) return exactMatch;

  // Fallback: find any item with matching SKU
  return cart.lineItems.find((item) => item.variant?.sku === variant.sku && item.price);
};
